import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { geocodeAddress } from "@/lib/map-utils";
import { formatPhoneNumber } from "@/lib/phoneUtils";

interface EditLocationSheetProps {
  isOpen: boolean;
  onClose: () => void;
  location: {
    _id: Id<"serviceLocations">;
    name: string;
    address: string;
    squareFootage: string;
    cleaningFrequency: string;
    serviceDays: string[];
    staffRequired?: number;
    status?: "active" | "inactive" | "pending";
    contactName?: string;
    contactTitle?: string;
    contactEmail?: string;
    contactPhone?: string;
  };
}

export function EditLocationSheet({ isOpen, onClose, location }: EditLocationSheetProps) {
  const [serviceDays, setServiceDays] = useState<string[]>(location.serviceDays || []);

  // Form state - initialize with location data
  const [formData, setFormData] = useState({
    name: location.name,
    address: location.address,
    squareFootage: location.squareFootage,
    staffRequired: location.staffRequired?.toString() || "",
    cleaningFrequency: location.cleaningFrequency,
    status: location.status || "active",
    contactName: location.contactName || "",
    contactTitle: location.contactTitle || "",
    contactEmail: location.contactEmail || "",
    contactPhone: location.contactPhone || "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Convex mutation
  const updateLocation = useMutation(api.serviceLocations.update);

  // Update form data when location prop changes
  useEffect(() => {
    setFormData({
      name: location.name,
      address: location.address,
      squareFootage: location.squareFootage,
      staffRequired: location.staffRequired?.toString() || "",
      cleaningFrequency: location.cleaningFrequency,
      status: location.status || "active",
      contactName: location.contactName || "",
      contactTitle: location.contactTitle || "",
      contactEmail: location.contactEmail || "",
      contactPhone: location.contactPhone || "",
    });
    setServiceDays(location.serviceDays || []);
  }, [location]);

  const handleInputChange = (field: string, value: string) => {
    // Apply phone formatting for phone fields
    const formattedValue = field === "contactPhone" ? formatPhoneNumber(value) : value;
    setFormData((prev) => ({
      ...prev,
      [field]: formattedValue,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.address || !formData.squareFootage) {
      return; // Basic validation
    }

    setIsSubmitting(true);

    try {
      // Geocode the address if it changed
      const addressChanged = formData.address !== location.address;
      const coordinates = addressChanged ? await geocodeAddress(formData.address) : undefined;

      await updateLocation({
        id: location._id,
        name: formData.name,
        address: formData.address,
        squareFootage: formData.squareFootage,
        cleaningFrequency: formData.cleaningFrequency,
        serviceDays: serviceDays,
        staffRequired: formData.staffRequired ? parseInt(formData.staffRequired) : undefined,
        status: formData.status as "active" | "inactive" | "pending",
        coordinates: coordinates || undefined,
        contactName: formData.contactName || undefined,
        contactTitle: formData.contactTitle || undefined,
        contactEmail: formData.contactEmail || undefined,
        contactPhone: formData.contactPhone || undefined,
      });

      onClose();
    } catch (error) {
      console.error("Failed to update location:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Edit Service Location</SheetTitle>
          <SheetDescription>Update the details for this service location.</SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            <div className="space-y-2">
              <Label htmlFor="name">Location Name</Label>
              <Input
                id="name"
                placeholder="e.g. Main Office, Warehouse"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                placeholder="Full address"
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="square-footage">Square Footage</Label>
                <Input
                  id="square-footage"
                  placeholder="e.g. 25000"
                  value={formData.squareFootage}
                  onChange={(e) => handleInputChange("squareFootage", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="staff">Staff Required</Label>
                <Input
                  id="staff"
                  type="number"
                  placeholder="Number of staff"
                  value={formData.staffRequired}
                  onChange={(e) => handleInputChange("staffRequired", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="frequency">Service Frequency</Label>
              <Select
                value={formData.cleaningFrequency}
                onValueChange={(value) => handleInputChange("cleaningFrequency", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1_per_week">1 time per week</SelectItem>
                  <SelectItem value="2_per_week">2 times per week</SelectItem>
                  <SelectItem value="3_per_week">3 times per week</SelectItem>
                  <SelectItem value="4_per_week">4 times per week</SelectItem>
                  <SelectItem value="5_per_week">5 times per week</SelectItem>
                  <SelectItem value="6_per_week">6 times per week</SelectItem>
                  <SelectItem value="7_per_week">7 times per week</SelectItem>
                  <SelectItem value="1_per_month">1 time per month</SelectItem>
                  <SelectItem value="2_per_month">2 times per month</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="serviceDays">Service Days</Label>
              <div className="flex flex-wrap gap-2">
                {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map(
                  (day) => (
                    <button
                      key={day}
                      type="button"
                      className={`rounded-full border px-3 py-1.5 text-sm transition-all ${
                        serviceDays.includes(day)
                          ? "border-blue-500 bg-blue-500 text-white"
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                      }`}
                      onClick={() => {
                        const newDays = serviceDays.includes(day)
                          ? serviceDays.filter((d) => d !== day)
                          : [...serviceDays, day];
                        setServiceDays(newDays);
                      }}
                    >
                      {day.substring(0, 3)}
                    </button>
                  )
                )}
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <h3 className="text-sm font-medium">Contact Information</h3>

              <div className="space-y-2">
                <Label htmlFor="contactName">Contact Name</Label>
                <Input
                  id="contactName"
                  placeholder="e.g. John Smith"
                  value={formData.contactName}
                  onChange={(e) => handleInputChange("contactName", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactTitle">Contact Title</Label>
                <Input
                  id="contactTitle"
                  placeholder="e.g. Site Manager, Facility Manager"
                  value={formData.contactTitle}
                  onChange={(e) => handleInputChange("contactTitle", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactEmail">Contact Email</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  placeholder="e.g. contact@example.com"
                  value={formData.contactEmail}
                  onChange={(e) => handleInputChange("contactEmail", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactPhone">Contact Phone</Label>
                <Input
                  id="contactPhone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.contactPhone}
                  onChange={(e) => handleInputChange("contactPhone", e.target.value)}
                  maxLength={14}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleInputChange("status", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              className="w-full"
              onClick={handleSubmit}
              disabled={
                isSubmitting || !formData.name || !formData.address || !formData.squareFootage
              }
            >
              {isSubmitting ? "Saving Changes..." : "Save Changes"}
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
